import React, { useState, useEffect, useCallback } from "react";
import { Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import "../../../src/styles.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";
import i18next from "i18next";
import { toast } from "react-toastify";
import moment from "moment";

import InventoryWiseTable from "./Tables/InventoryWiseTable";
import DateFilter from "../ui/DateFilter";
import { checkPermission } from "../../Utils/permissionHelper";
import ExportCasInOutReportModal from "../../app/SessionHistory/ExportCasInOutReportModal";

import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import ShimmerLoader from "../../loading";
import SearchBox from "../CustomWidgets/SearchBox";

const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(context, args), delay);
  };
};

const InventorySaleReport = () => {
  const { t, i18n } = useTranslation();
  const { Option } = Select;

  const company_id = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );

  // ⭐ TABLE LOADER ONLY
  const [tableLoading, setTableLoading] = useState(false);

  // ⭐ SEPARATE SEARCH LOADER
  const [searchLoading, setSearchLoading] = useState(false);

  const [saleSummary, setSaleSummary] = useState([]);

  const today = dayjs();

  const [startDate, setStartDate] = useState(
    today.startOf("month").format("YYYY-MM-DD"),
  );

  const [endDate, setEndDate] = useState(today.format("YYYY-MM-DD"));

  const [selectedRange, setSelectedRange] = useState("thisMonth");

  const [currentLimit, setCurrentLimit] = useState(10);
  const [currentOffset, setCurrentOffset] = useState(0);

  const [exportModalOpen, setExportModalOpen] = useState(false);

  // ⭐ SESSION FILTER STATES
  const [allSessions, setAllSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  const [searchByShortCode, setSearchByShortCode] = useState(false);
  const [query, setquery] = useState("");

  const [sessionLoader, setSessionLoader] = useState(false);

  useEffect(() => {
    GetSessions();

    GetInventorySaleSummary(
      query,
      startDate,
      endDate,
      selectedSession,
      currentOffset,
      currentLimit,
    );
  }, [currentLimit, currentOffset]);

  // ⭐ FETCH SESSION LIST
  const GetSessions = async () => {
    setSessionLoader(true);
    try {
      const response = await axiosInstance.post(
        URLS.GET_POS_SESSION_LIST,
        {
          company_id,
          lang: i18n.language === "ar" ? "ar_001" : "en_US",
        },
      );

      if (response?.data?.result?.status_code === 200) {
        setAllSessions(response?.data?.result?.data);
      } else {
        setAllSessions([]);
      }
    } catch (error) {
      console.log("Session fetch error:", error);
      setAllSessions([]);
    } finally {
      // ⭐ Ensures loader stops even if the API throws an error
      setSessionLoader(false);
    }
  };

  // ⭐ UPDATED API CALL
  const GetInventorySaleSummary = async (
    text,
    start,
    end,
    session_id,
    offset,
    limit,
    isSearch = false,
  ) => {
    // ⭐ DIFFERENT LOADERS
    if (isSearch) {
      setSearchLoading(true);
    } else {
      setTableLoading(true);
    }

    try {
      const response = await axiosInstance.post(
        URLS.GET_INVENTORY_WISE_SALES,
        {
          search: searchByShortCode ? "" : text,
          short_code: searchByShortCode ? text : "",
          company_id,
          offset,
          limit,
          start_date: start,
          end_date: end,
          session_id: session_id || "",
          lang: i18n.language === "ar" ? "ar_001" : "en_US",
        },
      );

      if (response?.data?.result?.status_code === 200) {
        setSaleSummary(response.data.result.data);
      } else {
        setSaleSummary([]);
      }
    } catch (error) {
      console.error("Inventory sale fetch error:", error);
    } finally {
      setTableLoading(false);
      setSearchLoading(false);
    }
  };

  // ⭐ SESSION CHANGE
  const onSessionChange = (value) => {
    setSelectedSession(value);

    setCurrentLimit(10);
    setCurrentOffset(0);

    setquery("");

    GetInventorySaleSummary("", startDate, endDate, value, 0, 10);
  };

  // ⭐ EXPORT
  // ⭐ UPDATED EXPORT FUNCTION WITH ACTIVE FILTERS
  const handleExport = async (dates) => {
    let payload = {
      lang: i18next.language === "ar" ? "ar_001" : "en_US",
      company_id: company_id,
      // Fallback to state date references if modal argument properties are missing
      start_date: dates?.startDate || startDate,
      end_date: dates?.endDate || endDate,

      // 🔥 Sync the search bar text fields
      search: searchByShortCode ? "" : query,
      short_code: searchByShortCode ? query : "",

      /* 💡 NOTE: If this specific component also uses session, category, or salesman filters, 
         uncomment and add the lines below as needed: */
      session_id: selectedSession || "",
      // category_id: selectedCategory || null,
      // salesman_ids: selectedSaleman ? [selectedSaleman] : null,
    };

    try {
      const response = await axiosInstance.post(
        URLS.GET_SALES_SUMMARY_REPORT,
        payload,
      );

      if (response?.data?.result?.status_code === 200) {
        setExportModalOpen(false);
        downloadFile(response?.data?.result?.file_base64);
      } else {
        // Safe check using optional chaining to avoid breaking on unexpected errors
        toast.error(response?.data?.result?.status || "Export failed");
      }
    } catch (error) {
      console.error("Sales summary export error:", error);
      toast.error("Something went wrong while exporting.");
    }
  };

  const downloadFile = (base64Data) => {
    try {
      let cleanBase64 = base64Data
        .trim()
        .replace(/^data:[\w/+-]+;base64,/, "")
        .replace(/\s/g, "");

      const binaryString = atob(cleanBase64);

      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const filename = `${t("inventory_wise_sale")}_${moment().format(
        "YYYY-MM-DD_HH.mm.ss",
      )}.xlsx`;

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = filename;
      link.click();

      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch {
      alert("Download failed");
    }
  };

  // ⭐ DEBOUNCED SEARCH
  const debouncedSearch = useCallback(
    debounce((value) => {
      GetInventorySaleSummary(
        value,
        startDate,
        endDate,
        null,
        0,
        10,
        true,
      );
    }, 1000),
    [searchByShortCode, startDate, endDate],
  );

  return (
    <>
      <div className="w-full flex flex-col pb-2 overflow-y-auto mt-3">

        {/* FILTER ROW */}
        <div className="flex items-center justify-between">

          <div className="flex items-center gap-3">

            {/* SESSION FILTER */}
            <Select
              showSearch
              optionFilterProp="children"
              className="overflow-hidden mt-2"
              style={{ width: 250, height: 40 }}
              placeholder={t("All Sessions")}
              value={selectedSession ?? null}
              onChange={onSessionChange}
              loading={sessionLoader}     
              disabled={sessionLoader}     
            >
              <Option value={null}>{t("All Sessions")}</Option>

              {allSessions.map((s) => (
                <Option key={s?.id} value={s?.id}>
                  {s?.name}
                </Option>
              ))}
            </Select>

            {/* DATE FILTER */}
            <DateFilter
              setSelectedRange={setSelectedRange}
              selectedRange={selectedRange}
              SubmitFilter={(start, end) => {
                setStartDate(start);
                setEndDate(end);

                setquery("");

                setCurrentLimit(10);
                setCurrentOffset(0);

                GetInventorySaleSummary(
                  "",
                  start,
                  end,
                  selectedSession,
                  0,
                  10,
                );
              }}
            />
          </div>

          {/* EXPORT BUTTON */}
          <button
            className="w-48 h-10 mt-2 bg-primaryBlue text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
            onClick={() => {
              if (checkPermission("/inventory/inventorysalesummary")) {
                setExportModalOpen(true);
              }
            }}
          >
            <DownloadOutlined style={{ fontSize: "1.5em" }} />

            <span>{t("Download in Excel")}</span>
          </button>
        </div>

        {/* SEARCH + CLEAR */}
        <div className="flex justify-between items-end">

          <div className="flex gap-x-2 mt-3">

            <div>
              <SearchBox
                value={query}
                loading={searchLoading} // ⭐ SEARCH LOADER ONLY
                searchResult={(values) => {
                  setquery(values);

                  setSelectedSession(null);

                  debouncedSearch(values);
                }}
                placeholder={t("search_product")}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                setSearchByShortCode(!searchByShortCode);
              }}
              className={`h-[40px] px-2 rounded-md border text-sm whitespace-nowrap ${
                searchByShortCode
                  ? "bg-blue-100 border-primaryBlue text-primaryBlue"
                  : "bg-white border-gray-300 text-gray-700"
              }`}
            >
              {t("Search Code")}
            </button>
          </div>

          {/* CLEAR FILTER */}
          <span
            onClick={() => {
              setSelectedSession(null);

              const today = dayjs();

              const defaultStart = today
                .startOf("month")
                .format("YYYY-MM-DD");

              const defaultEnd = today.format("YYYY-MM-DD");

              setStartDate(defaultStart);
              setEndDate(defaultEnd);

              setSelectedRange("thisMonth");

              setCurrentLimit(10);
              setCurrentOffset(0);

              setquery("");

              GetInventorySaleSummary(
                "",
                defaultStart,
                defaultEnd,
                null,
                0,
                10,
              );
            }}
            className="flex flex-row justify-end font-semibold text-sm text-primaryBlue cursor-pointer mt-5 me-4"
          >
            {t("Clear Filter")}
          </span>
        </div>

        {/* TABLE */}
        {tableLoading ? (
          <ShimmerLoader />
        ) : (
          <InventoryWiseTable
            additionalData={saleSummary}
            pagination={(p) => {
              setCurrentLimit(p.limit);
              setCurrentOffset(p.offset);
            }}
          />
        )}
      </div>

      {/* EXPORT MODAL */}
      {exportModalOpen && (
        <ExportCasInOutReportModal
          heading={t("Download Inventory Wise Sale")}
          isOpen={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          onExport={handleExport}
        />
      )}
    </>
  );
};

export default InventorySaleReport;
// import React, { useState, useEffect } from "react";
// import { Breadcrumb, Button, Drawer, Select } from "antd";
// import { DoubleRightOutlined, DownloadOutlined } from "@ant-design/icons";
// import "../../../src/styles.css";
// import { useSelector } from "react-redux";
// import { useTranslation } from "react-i18next";
// import { Link, useLocation } from "react-router-dom";

// import dayjs from "dayjs";
// import i18next from "i18next";
// import { toast } from "react-toastify";
// import moment from "moment";
// import InventoryWiseTable from "./Tables/InventoryWiseTable";
// import DateFilter from "../ui/DateFilter";
// import { checkPermission } from "../../Utils/permissionHelper";
// import { GetPaymentMethods } from "../../API  Functions/CashierSetup";
// import ExportCasInOutReportModal from "../../app/SessionHistory/ExportCasInOutReportModal";
// import axiosInstance from "../../AxiosInstance";
// import { URLS } from "../../API/API endpoints";
// import { Navbar } from "@material-tailwind/react";
// import ShimmerLoader from "../../loading";

// const InventorySaleReport = () => {
//     const { t, i18n } = useTranslation();
//     const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
//     const [loading, setloading] = useState(false);
//     const { Option } = Select;
//     const [currentLimit, setCurrentLimit] = useState(10); // Default limit
//     const [currentOffset, setCurrentOffset] = useState(0); // Default offset
//     const [selectedRange, setSelectedRange] = useState("thisMonth");
//     const [saleSummary, setSaleSummary] = useState([]);
//     const today = dayjs();
//     const formatToday = today.format("YYYY-MM-DD");
//     const startofMonth = today.startOf("month").format("YYYY-MM-DD");
//     const [startDate, setStartDate] = useState(startofMonth);
//     const [endDate, setEndDate] = useState(formatToday);
//     const [exportModalOpen, setExportModalOpen] = useState(false);

//     const company_id = useSelector(
//         (state) => state?.login?.user?.result?.company_id
//     );
//     const [selectedCategory, setSelectedCategory] = useState(null);
//     const [selectedSubcategory, setSelectedSubcategory] = useState(null);
//     const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

//     useEffect(() => {;
//         GetInventorySaleSummary(
//             selectedCategory,
//             selectedSubcategory,
//             selectedPaymentMethod,
//             startDate,
//             endDate,
//             currentOffset,
//             currentLimit
//         );
//     }, [currentLimit, currentOffset]);



//     const GetInventorySaleSummary = async (
//         cat,
//         subCat,
//         method,
//         start,
//         end,
//         offset,
//         limit
//     ) => {
//         try {
//             const response = await axiosInstance.post(URLS.GET_INVENTORY_WISE_SALES, {
//                 company_id: company_id,
//                 offset: offset,
//                 limit: limit,
//                 start_date: start,
//                 end_date: end,
//                 lang: i18n.language == "ar" ? "ar_001" : "en_US",
//             });
//             if (response?.data?.result?.status_code === 200) {
//                 setSaleSummary(response?.data?.result?.data);
//             } else {
//                 setSaleSummary([]);
//             }
//         } catch (error) {
//             console.error("Error fetching KPI data:", error);
//             setloading(false);
//         } finally {
//             setloading(false);
//         }
//     };

//     const handleExport = async (dates) => {
//         let payload = {
//             lang: i18next.language === "ar" ? "ar_001" : "en_US",
//             start_date: dates?.startDate,
//             end_date: dates?.endDate,
//             company_id: company_id,
//         };
//         const response = await axiosInstance.post(
//             URLS.CASHIER_INVENTORY_SALE_SUMMARY_EXCEL,
//             payload
//         );
//         console.log("handleExport", response);
//         if (response?.data?.result?.status_code == 200) {
//             setExportModalOpen(false);
//             downloadFile(response?.data?.result?.file_base64);
//         } else {
//             toast.error(response?.data?.result.status);
//         }
//     };

//     const downloadFile = (base64Data) => {
//         try {
//             if (
//                 !base64Data ||
//                 typeof base64Data !== "string" ||
//                 base64Data.trim() === ""
//             ) {
//                 alert("No valid file data available for download");
//                 return;
//             }

//             let cleanBase64 = base64Data
//                 .trim()
//                 .replace(/^data:[\w/+-]+;base64,/, "")
//                 .replace(/^["']|["']$/g, "")
//                 .replace(/\s/g, "");

//             const padLength = 4 - (cleanBase64.length % 4);
//             if (padLength < 4) cleanBase64 += "=".repeat(padLength);

//             let binaryString = atob(cleanBase64);
//             const bytes = new Uint8Array(binaryString.length);
//             for (let i = 0; i < binaryString.length; i++) {
//                 bytes[i] = binaryString.charCodeAt(i);
//             }

//             const header = new Uint8Array(bytes.buffer, 0, 8);
//             let mimeType = "application/octet-stream";
//             let fileExtension = "bin";

//             if (header[0] === 0x50 && header[1] === 0x4b) {
//                 mimeType =
//                     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
//                 fileExtension = "xlsx";
//             } else if (
//                 header[0] === 0xd0 &&
//                 header[1] === 0xcf &&
//                 header[2] === 0x11 &&
//                 header[3] === 0xe0
//             ) {
//                 mimeType = "application/vnd.ms-excel";
//                 fileExtension = "xls";
//             } else if (new TextDecoder().decode(header).includes(",")) {
//                 mimeType = "text/csv";
//                 fileExtension = "csv";
//             }

//             const blob = new Blob([bytes], { type: mimeType });
//             const timestamp = moment().format("YYYY-MM-DD_HH.mm.ss"); // Using period for time
//             const filename = `${t(
//                 "inventory_wise_sale"
//             )}_${timestamp}.${fileExtension}`;

//             const url = URL.createObjectURL(blob);

//             const link = document.createElement("a");
//             link.href = url;
//             link.download = filename;
//             document.body.appendChild(link);
//             link.click();
//             setTimeout(() => {
//                 document.body.removeChild(link);
//                 URL.revokeObjectURL(url);
//             }, 100);
//         } catch (error) {
//             console.error("File download failed:", error);
//             alert(`Download failed: ${error.message || "Unknown error"}`);
//         }
//     };

//     const handleMethodOptionChange = (id, selectedValue) => {
//         console.log("selectedValue===>", selectedValue, id);

//         setloading(true);
//         if (id) {
//             setSelectedPaymentMethod(selectedValue?.children);
//             if (selectedValue?.children) {
//                 GetInventorySaleSummary(
//                     selectedCategory,
//                     selectedSubcategory,
//                     selectedValue?.children,
//                     startDate,
//                     endDate
//                 );
//             }
//         } else {
//             setSelectedPaymentMethod(null); // Reset selected category
//             GetInventorySaleSummary(
//                 selectedCategory,
//                 selectedSubcategory,
//                 null,
//                 startDate,
//                 endDate,
//                 0,
//                 10
//             ); // Fetch all products
//         }
//     };


   

//     return (
//         <>
//             <div className="w-full flex flex-col md:h-full pb-2 overflow-y-auto ">

//                     <div className=" mt-3">
//                         <div className="flex items-center justify-between">
//                             <DateFilter
//                                 setSelectedRange={setSelectedRange}
//                                 selectedRange={selectedRange}
//                                 SubmitFilter={(start, end) => {
//                                     setloading(true);
//                                     setStartDate(start);
//                                     setEndDate(end);
//                                     setSelectedCategory(null);
//                                     setSelectedSubcategory(null);
//                                     setSelectedPaymentMethod("");
//                                     setCurrentLimit(10);
//                                     setCurrentOffset(0);
//                                     GetInventorySaleSummary(null, null, "", start, end, 0, 10); // Fetch all products
//                                 }}
//                             />
//                             <div className=" flex justify-end mt-3 ">
//                                 <button
//                                     className="w-48 h-10 bg-primaryBlue text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
//                                     // onClick={() => setExportModalOpen(true)}
//                                     onClick={() => {
//                                         if (checkPermission("/inventory/inventorysalesummary")) {
//                                             setExportModalOpen(true);
//                                         }
//                                     }}
//                                 >
//                                     <DownloadOutlined
//                                         style={{ fontSize: "1.5em", MozMarginStart: -10 }}
//                                     />

//                                     <span>{t("Download in Excel")}</span>
//                                 </button>
//                             </div>
//                         </div>

//                         {loading ? (
//                             <ShimmerLoader />
//                         ) : (
//                             <>
//                                 <InventoryWiseTable
//                                     additionalData={saleSummary}
//                                     pagination={(values) => {
//                                         // setloading(true);
//                                         console.log("values=---------->", values);

//                                         setCurrentLimit(values.limit);
//                                         setCurrentOffset(values.offset);
//                                     }}
//                                 />
//                             </>
//                         )}
//                     </div>
//                 </div>
//             {/* </div> */}

//             {exportModalOpen && (
//                 <ExportCasInOutReportModal
//                     heading={t("Download Inventory Wise Sale")}
//                     isOpen={exportModalOpen}
//                     onClose={() => setExportModalOpen(false)}
//                     onExport={(dates) => handleExport(dates)}
//                 />
//             )}
//         </>
//     );
// };

// export default InventorySaleReport;
